import { useEffect, useMemo } from 'react'

import type { ResolvedShellProduct, ResolvedVariant } from 'models/purchaseFlowRevamp'

import { uploadedFilesForProductCode, usePfV2VisualUpload } from '../../fileHub'
import { usePfStore } from '../../store'
import { claimedFilenames } from '../../store/slices/cartAttachmentsSlice'
import { configurationOwnsUpload, drawerShowsUpload, isMissingRequiredUploads } from './uploadRequirement'

/**
 * The files the open drawer speaks for: this product group's uploads minus what another cart line
 * already owns. The listing, the quantity of a one-per-item product and the snapshot the line
 * stores all read `ownUploadedFiles` and nothing else.
 *
 * A one-per-item (`EXACTLY_COUNT`) product's quantity follows its upload count, because the order
 * validator rejects a line whose image count differs from its quantity.
 */
export const useDrawerUploads = (shell: ResolvedShellProduct | null, selectedVariant: ResolvedVariant | undefined) => {
  const isOpen = usePfStore((s) => s.isOpen)
  const editingCartItemId = usePfStore((s) => s.editingCartItemId)
  const shellProductId = usePfStore((s) => s.shellProductId)
  const quantity = usePfStore((s) => s.quantity)
  const setQuantity = usePfStore((s) => s.setQuantity)
  const attachments = usePfStore((s) => s.attachments)
  const { stagingEntries } = usePfV2VisualUpload()

  /** Whether the drawer collects the upload; a configuration module may own it instead. */
  const showUploadSection = !!shell?.requiresUpload && !configurationOwnsUpload(shell?.configurationFlows)
  /** Whether the drawer renders a dropzone, and with it the configuration section. */
  const showDrawerUpload = drawerShowsUpload({
    requiresUpload: !!shell?.requiresUpload,
    configurationFlows: shell?.configurationFlows,
  })

  const claimedByOtherLines = useMemo(
    () => claimedFilenames(attachments, editingCartItemId),
    [attachments, editingCartItemId]
  )

  const ownUploadedFiles = useMemo(
    () => uploadedFilesForProductCode(stagingEntries, shellProductId)
      .filter((file) => !claimedByOtherLines.has(file.gcFilename)),
    [stagingEntries, shellProductId, claimedByOtherLines]
  )

  const isOnePerItem = selectedVariant?.inputImagePolicy === 'EXACTLY_COUNT'
  const uploadedCount = ownUploadedFiles.length
  const uploadsMissing = isMissingRequiredUploads({
    requiresUpload: showUploadSection,
    policy: selectedVariant?.inputImagePolicy,
    uploadedCount,
    quantity,
  })

  useEffect(() => {
    if (!isOpen || !isOnePerItem || uploadedCount === 0) return
    if (quantity === uploadedCount) return
    setQuantity(uploadedCount)
  }, [isOpen, isOnePerItem, uploadedCount, quantity, setQuantity])

  return { showUploadSection, showDrawerUpload, claimedByOtherLines, ownUploadedFiles, uploadsMissing }
}
